/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BriefcaseBusiness, CopyPlus } from "lucide-react";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { showAlert, showErrorAlert } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import CatalogView, { CatalogSkeleton, CatalogUnavailable } from "../../../../components/catalog/CatalogView.jsx";
import CatalogItemViewer from "../../../../components/catalog/CatalogItemViewer.jsx";
import { itemEnquiryText, whatsappUrl } from "../../../../components/catalog/catalogUtils.js";
import { editorPathForId } from "../../../../products/registry";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

/**
 * Proyectos que el equipo le ha preparado al usuario (C3D-38): los ve como un
 * catálogo y puede copiarse un diseño para retocarlo en el customizador.
 */
function useMyProject(projectId) {
    const [state, setState] = useState({ loading: true, error: null, project: null });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, error: null, project: null });
        axios.get(`/my-projects/${projectId}/`)
            .then(({ data }) => { if (!cancelled) setState({ loading: false, error: null, project: data }); })
            .catch((error) => {
                if (!cancelled) setState({ loading: false, error: error?.response?.status === 404 ? "not_found" : "network", project: null });
            });
        return () => { cancelled = true; };
    }, [projectId, attempt]);
    return { ...state, retry: () => setAttempt((value) => value + 1) };
}

function ProjectError({ error, retry }) {
    return error === "not_found" ? (
        <CatalogUnavailable
            title="Este proyecto no está disponible"
            text="Puede que ya no esté asignado a tu cuenta."
            action={<Link to="/profile/proyectos" className="btn btn-primary btn-sm">Ver mis proyectos</Link>}
        />
    ) : (
        <CatalogUnavailable
            title="No hemos podido cargar el proyecto"
            text="Revisa tu conexión e inténtalo otra vez."
            action={<button type="button" className="btn btn-primary btn-sm" onClick={retry}>Reintentar</button>}
        />
    );
}

/** Copia el diseño a la cuenta del usuario y lo abre en su customizador. */
function useCopyToMyDesigns(projectId) {
    const navigate = useNavigate();
    const [copyingId, setCopyingId] = useState(null);
    const copy = useCallback(async (item) => {
        setCopyingId(item.id);
        try {
            const { data } = await axios.post(`/my-projects/${projectId}/items/${item.id}/copy/`);
            const result = await showAlert({
                title: "Copiado a tus diseños",
                text: `"${data.name}" ya está en Modelos creados. ¿Quieres abrirlo para retocarlo?`,
                icon: "success",
                confirmButtonText: "Abrir en el customizador",
                showCancelButton: true,
                cancelButtonText: "Seguir aquí",
            });
            if (result?.isConfirmed) navigate(editorPathForId(data.id, data.product_type));
        } catch (error) {
            showErrorAlert({ text: error?.response?.data?.detail || "No se ha podido copiar el diseño." });
        } finally {
            setCopyingId(null);
        }
    }, [navigate, projectId]);
    return { copy, copyingId };
}

/** `/profile/proyectos`: la lista. */
export function MisProyectosCliente() {
    const [state, setState] = useState({ loading: true, error: false, projects: [] });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get("/my-projects/")
            .then(({ data }) => { if (!cancelled) setState({ loading: false, error: false, projects: data.results ?? data }); })
            .catch(() => { if (!cancelled) setState({ loading: false, error: true, projects: [] }); });
        return () => { cancelled = true; };
    }, [attempt]);

    return (
        <div className="w-full max-w-5xl mx-auto p-4 flex flex-col gap-6">
            <header className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-base-content">Mis proyectos</h1>
                <p className="text-sm text-base-content/70 max-w-[65ch]">
                    Los diseños que hemos preparado para ti. Puedes verlos en 3D, copiarlos a tus diseños para retocarlos o pedirlos.
                </p>
            </header>

            {state.loading && (
                <div className="grid gap-4 sm:grid-cols-2" aria-busy="true">
                    {[0, 1].map((key) => <div key={key} className="skeleton h-40 rounded-xl" />)}
                </div>
            )}

            {!state.loading && state.error && (
                <div role="alert" className="alert alert-error justify-between">
                    <span>No hemos podido cargar tus proyectos.</span>
                    <button type="button" className="btn btn-sm" onClick={() => setAttempt((v) => v + 1)}>Reintentar</button>
                </div>
            )}

            {!state.loading && !state.error && state.projects.length === 0 && (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <BriefcaseBusiness className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold text-base-content">Todavía no tienes proyectos</p>
                    <p className="text-sm text-base-content/70 max-w-sm">
                        Cuando te preparemos diseños a medida, aparecerán aquí.
                    </p>
                </div>
            )}

            {!state.loading && !state.error && state.projects.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2">
                    {state.projects.map((project) => (
                        <Link
                            key={project.id}
                            to={`/profile/proyectos/${project.id}`}
                            className="group flex flex-col rounded-xl border border-base-content/10 bg-base-100 overflow-hidden hover:border-primary/50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                        >
                            <div className="grid grid-cols-4 gap-1 bg-base-200 p-2 h-28">
                                {project.preview_images.length ? project.preview_images.map((url) => (
                                    <img key={url} src={getMediaUrl(url)} alt="" className="w-full h-full object-contain" loading="lazy" />
                                )) : <span className="col-span-4 self-center text-center text-sm text-base-content/50">Sin imágenes</span>}
                            </div>
                            <div className="p-4 flex flex-col gap-1">
                                <span className="font-semibold text-base-content group-hover:text-primary">{project.name}</span>
                                <span className="text-sm text-base-content/70">
                                    {project.item_count} {project.item_count === 1 ? "modelo" : "modelos"}
                                    {project.contact_name ? `, te atiende ${project.contact_name}` : ""}
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

/** `/profile/proyectos/:projectId`: el catálogo del proyecto. */
export function MiProyectoCliente() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { loading, error, project, retry } = useMyProject(projectId);
    const { copy, copyingId } = useCopyToMyDesigns(projectId);

    const downloadPdf = useCallback(async () => {
        try {
            const { data } = await axios.get(`/my-projects/${projectId}/pdf/`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `catalogo-${project?.name || projectId}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            showErrorAlert({ text: "No se ha podido descargar el PDF." });
        }
    }, [project?.name, projectId]);

    if (loading) return <CatalogSkeleton />;
    if (error) return <ProjectError error={error} retry={retry} />;

    return (
        <CatalogView
            catalog={project}
            onDownloadPdf={downloadPdf}
            onView3d={(item) => navigate(`/mis-proyectos/${projectId}/modelo/${item.id}`)}
            headerSlot={(
                <Link to="/profile/proyectos" className="btn btn-ghost btn-sm gap-2 self-start">
                    <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Mis proyectos
                </Link>
            )}
            renderExtraAction={(item) => (item.kind === "design" ? (
                <button
                    type="button"
                    className="btn btn-sm btn-ghost gap-2 w-full"
                    onClick={() => copy(item)}
                    disabled={copyingId === item.id}
                >
                    {copyingId === item.id
                        ? <span className="loading loading-spinner loading-xs" aria-hidden="true" />
                        : <CopyPlus className="w-4 h-4" aria-hidden="true" />}
                    Copiar a mis diseños
                </button>
            ) : null)}
        />
    );
}

/** `/mis-proyectos/:projectId/modelo/:itemId`: un modelo del proyecto en 3D. */
export function MiProyectoModelo() {
    const { projectId, itemId } = useParams();
    const navigate = useNavigate();
    const { loading, error, project, retry } = useMyProject(projectId);
    const { copy } = useCopyToMyDesigns(projectId);
    const back = () => navigate(`/profile/proyectos/${projectId}`);

    if (loading) return <CatalogSkeleton />;
    if (error) return <ProjectError error={error} retry={retry} />;

    const item = project.items.find((candidate) => String(candidate.id) === String(itemId));
    if (!item || !item.has_3d) {
        return (
            <CatalogUnavailable
                title="Este modelo no se puede ver en 3D"
                text="Vuelve al proyecto para ver el resto de modelos."
                action={<button type="button" className="btn btn-primary btn-sm" onClick={back}>Volver al proyecto</button>}
            />
        );
    }

    return (
        <CatalogItemViewer
            glbUrl={`${API_BASE_URL}my-projects/${projectId}/items/${item.id}/glb/`}
            withAuth
            title={item.title}
            onBack={back}
            backLabel="Volver al proyecto"
            enquiryUrl={whatsappUrl(project.contact?.whatsapp_phone, itemEnquiryText(project.name, item))}
            extraActions={[{
                key: "copy",
                icon: DocumentDuplicateIcon,
                label: "Copiar a mis diseños",
                mobileLabel: "Copiar",
                tip: "Quedarte una copia para retocarla",
                onClick: () => copy(item),
            }]}
        />
    );
}
